import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import styles from './VNTransfertabsSideModal.module.scss'
import BackChevron from '../../../components/BackChevron'
import SidebarModal from '../../../components/SidebarModal'
import Button from '../../../components/Button'
import { APPLICATION_STRINGS } from '../../../strings'
import { useTransferTab } from '../../../VNTransferTabs/hooks/useTransferTab'
import VNSearch from '../../../VNCategories/components/VNSearch'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getTransferTabModal } from '../../Selectors'
import { setTransferTabModal } from '../../ActionCreators'
import { setManagerApprovalPrompt } from '../../../VNManagerApproval/ActionCreators'

const MIN_QUERY_LENGTH = 2

export const TransferTabsSideModal = () => {
  const props = useSelector(getTransferTabModal)
  const dispatch = useDispatch()
  const classNames = styles.transferTabsModal
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedAttendant, setSelectedAttendant] = useState(null)
  const [searchActive, setSearchActive] = useState(false)
  const searchRef = useRef(null)

  const { transferTab, searchNames } = useTransferTab()

  useEffect(() => {
    if (!props.open) {
      setSearchValue('')
      setSearchResults([])
      setSelectedAttendant(null)
    }
  }, [props.open])

  useEffect(() => {
    if (selectedAttendant && !searchResults.some((attendant) => attendant?.id === selectedAttendant?.id)) {
      setSelectedAttendant(null)
    }
  }, [searchValue, searchResults, selectedAttendant])

  const onClose = useCallback(() => {
    dispatch(setTransferTabModal({ open: false }))
    setSearchValue('')
    setSearchResults([])
    setSelectedAttendant(null)
  }, [dispatch])

  const searchAttendantNames = useCallback(
    (query) => {
      setSearchValue(query)
      setSearchResults(searchNames(query))
    },
    [searchNames],
  )

  const displayResults = useMemo(() => {
    if (searchValue.length === 0) {
      return null
    }

    if (searchResults?.length === 0 && searchValue.length >= MIN_QUERY_LENGTH) {
      return <div className={cn(styles.helperText, styles.searchStatus)}>{APPLICATION_STRINGS.TRANSFER_TABS_NO_ATTENDANTS}</div>
    }

    if (searchResults?.length > 30 && searchValue.length > 0) {
      return (
        <div className={cn(styles.helperText, styles.searchStatus)}>{APPLICATION_STRINGS.TRANSFER_TABS_TOO_MANY_RESULTS}</div>
      )
    }

    return searchResults.map((attendant) => (
      <div
        key={attendant?.id}
        className={cn(styles.attendantName, { [styles.selectedAttendant]: selectedAttendant?.id === attendant?.id })}
        onClick={() => {
          if (attendant?.id === selectedAttendant?.id) {
            setSelectedAttendant(null)
            searchRef.current && searchRef.current.hideKeyboard()
            return
          }
          setSelectedAttendant(attendant)
          searchRef.current && searchRef.current.hideKeyboard()
        }}
      >
        {attendant?.name}
      </div>
    ))
  }, [searchResults, searchValue, selectedAttendant])

  const onManagerApprover = (approverId) => {
    transferTab(props.order, selectedAttendant?.id, selectedAttendant?.name, approverId, onClose)
  }

  const onTransferTab = () => {
    if (!selectedAttendant) return

    dispatch(
      setManagerApprovalPrompt({
        onSuccess: onManagerApprover,
        buttonText: APPLICATION_STRINGS.TRANSFER_TABS_BUTTON_NAME,
      }),
    )
  }

  const isButtonDisabled = useMemo(() => {
    return !selectedAttendant
  }, [selectedAttendant])

  if (!props?.open) {
    return null
  }

  return (
    <SidebarModal
      show
      title="Transfer Tab"
      onClose={onClose}
      leftAccessory={<BackChevron onClick={onClose} />}
      className={classNames}
      contentStyle={styles.sideBarModalContent}
    >
      <div className={styles.transferTabsModal}>
        <div className={styles.helperText}>{APPLICATION_STRINGS.TRANSFER_TABS_SUBTEXT}</div>
        <VNSearch
          setSearchActive={setSearchActive}
          searchRef={searchRef}
          emptySearchPlaceholder="Search attendants by name"
          setParentValue={searchAttendantNames}
        />
        <div className={styles.transferTabsContainer}>{displayResults}</div>
        <div className={styles.gradient}></div>
        <div className={styles.submitContainer}>
          <Button
            className={cn(styles.submitButton, searchActive && styles.hidden)}
            disabled={isButtonDisabled}
            onClick={onTransferTab}
          >
            Transfer Tab
          </Button>
        </div>
      </div>
    </SidebarModal>
  )
}
